import { createVNode as _createVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, mergeProps as _mergeProps } from "vue";
// Styles
import "./VAlert.css";

// Components
import { VAlertTitle } from "./VAlertTitle.js";
import { VExpandTransition } from "../transitions/index.js";
import { VBtn } from "../VBtn/index.js";
import { VDefaultsProvider } from "../VDefaultsProvider/index.js";
import { VIcon } from "../VIcon/index.js";
import { VProgressCircular } from "../VProgressCircular/index.js";
import { VProgressLinear } from "../VProgressLinear/index.js"; // Composables
import { useAutoDismiss } from "./autoDismiss.js";
import { useTextColor } from "../../composables/color.js";
import { makeComponentProps } from "../../composables/component.js";
import { makeDensityProps, useDensity } from "../../composables/density.js";
import { makeDimensionProps, useDimension } from "../../composables/dimensions.js";
import { makeElevationProps, useElevation } from "../../composables/elevation.js";
import { IconValue } from "../../composables/icons.js";
import { makeIconSizeProps, useIconSizes } from "../../composables/iconSizes.js";
import { useLocale } from "../../composables/locale.js";
import { makeLocationProps, useLocation } from "../../composables/location.js";
import { makePositionProps, usePosition } from "../../composables/position.js";
import { useProxiedModel } from "../../composables/proxiedModel.js";
import { makeRoundedProps, useRounded } from "../../composables/rounded.js";
import { makeTagProps } from "../../composables/tag.js";
import { makeThemeProps, provideTheme } from "../../composables/theme.js";
import { makeTransitionProps, MaybeTransition } from "../../composables/transition.js";
import { genOverlays, makeVariantProps, useVariant } from "../../composables/variant.js"; // Utilities
import { toRef } from 'vue';
import { genericComponent, isBoolean, propsFactory } from "../../util/index.js"; // Types
const allowedTypes = ['success', 'info', 'warning', 'error'];
export const makeVAlertProps = propsFactory({
  border: {
    type: [Boolean, String],
    validator: val => {
      return isBoolean(val) || ['top', 'end', 'bottom', 'start'].includes(val);
    }
  },
  borderColor: String,
  closable: Boolean,
  closeIcon: {
    type: IconValue,
    default: '$close'
  },
  closeLabel: {
    type: String,
    default: '$vuetify.close'
  },
  icon: {
    type: [Boolean, String, Function, Object],
    default: null
  },
  modelValue: {
    type: Boolean,
    default: true
  },
  prominent: Boolean,
  title: String,
  text: String,
  timeout: {
    type: [Number, String],
    default: -1
  },
  timer: [Boolean, String],
  type: {
    type: String,
    validator: val => allowedTypes.includes(val)
  },
  ...makeComponentProps(),
  ...makeDensityProps(),
  ...makeDimensionProps(),
  ...makeElevationProps(),
  ...makeIconSizeProps(),
  ...makeLocationProps(),
  ...makePositionProps(),
  ...makeRoundedProps(),
  ...makeTagProps(),
  ...makeThemeProps(),
  ...makeTransitionProps({
    transition: {
      component: VExpandTransition
    }
  }),
  ...makeVariantProps({
    variant: 'flat'
  })
}, 'VAlert');
export const VAlert = genericComponent()({
  name: 'VAlert',
  props: makeVAlertProps(),
  emits: {
    'click:close': e => true,
    'update:modelValue': value => true
  },
  setup(props, {
    emit,
    slots
  }) {
    const isActive = useProxiedModel(props, 'modelValue');
    const icon = toRef(() => {
      if (props.icon === false) return undefined;
      if (!props.type) return props.icon;
      return props.icon ?? `$${props.type}`;
    });
    const {
      iconSize
    } = useIconSizes(props, () => props.prominent ? 44 : undefined);
    const {
      themeClasses
    } = provideTheme(props);
    const {
      colorClasses,
      colorStyles,
      variantClasses
    } = useVariant(() => ({
      color: props.color ?? props.type,
      variant: props.variant
    }));
    const {
      densityClasses
    } = useDensity(props);
    const {
      dimensionStyles
    } = useDimension(props);
    const {
      elevationClasses
    } = useElevation(props);
    const {
      locationStyles
    } = useLocation(props);
    const {
      positionClasses
    } = usePosition(props);
    const {
      roundedClasses,
      roundedStyles
    } = useRounded(props);
    const {
      textColorClasses,
      textColorStyles
    } = useTextColor(() => props.borderColor);
    const {
      t
    } = useLocale();
    const {
      rootEl,
      progress,
      events
    } = useAutoDismiss(isActive, () => Number(props.timeout));
    const closeProps = toRef(() => ({
      'aria-label': t(props.closeLabel),
      onClick(e) {
        isActive.value = false;
        emit('click:close', e);
      }
    }));
    return () => {
      const hasPrepend = !!(slots.prepend || icon.value);
      const hasTitle = !!(slots.title || props.title);
      const hasClose = !!(slots.close || props.closable);
      const hasTimer = !!props.timer && Number(props.timeout) > 0;
      const iconProps = {
        density: props.density,
        icon: icon.value,
        size: props.iconSize || props.prominent ? iconSize.value : undefined
      };
      return _createVNode(MaybeTransition, {
        "transition": props.transition
      }, {
        default: () => [isActive.value && _createVNode(props.tag, _mergeProps({
          "ref": rootEl,
          "class": ['v-alert', props.border && {
            'v-alert--border': !!props.border,
            [`v-alert--border-${props.border === true ? 'start' : props.border}`]: true
          }, {
            'v-alert--prominent': props.prominent
          }, themeClasses.value, colorClasses.value, densityClasses.value, elevationClasses.value, positionClasses.value, roundedClasses.value, variantClasses.value, props.class],
          "style": [colorStyles.value, dimensionStyles.value, locationStyles.value, roundedStyles.value, props.style],
          "role": "alert"
        }, events), {
          default: () => [genOverlays(false, 'v-alert'), hasTimer && props.timer !== 'close' && _createVNode(VProgressLinear, {
            "key": "timer",
            "class": "v-alert__timer",
            "absolute": true,
            "location": props.timer === 'bottom' ? 'bottom' : 'top',
            "modelValue": progress.value,
            "shape": "flat"
          }, null), props.border && _createElementVNode("div", {
            "key": "border",
            "class": _normalizeClass(['v-alert__border', textColorClasses.value]),
            "style": _normalizeStyle(textColorStyles.value)
          }, null), hasPrepend && _createElementVNode("div", {
            "key": "prepend",
            "class": "v-alert__prepend"
          }, [!slots.prepend ? _createVNode(VIcon, _mergeProps({
            "key": "prepend-icon"
          }, iconProps), null) : _createVNode(VDefaultsProvider, {
            "key": "prepend-defaults",
            "disabled": !icon.value,
            "defaults": {
              VIcon: {
                ...iconProps
              }
            }
          }, slots.prepend)]), _createElementVNode("div", {
            "class": "v-alert__content"
          }, [hasTitle && _createVNode(VAlertTitle, {
            "key": "title"
          }, {
            default: () => [slots.title?.() ?? props.title]
          }), slots.text?.() ?? props.text, slots.default?.()]), slots.append && _createElementVNode("div", {
            "key": "append",
            "class": "v-alert__append"
          }, [slots.append()]), hasClose && _createElementVNode("div", {
            "key": "close",
            "class": "v-alert__close"
          }, [!slots.close ? _createVNode(VBtn, _mergeProps({
            "key": "close-btn",
            "icon": props.closeIcon,
            "size": "x-small",
            "variant": "text"
          }, closeProps.value), null) : _createVNode(VDefaultsProvider, {
            "key": "close-defaults",
            "defaults": {
              VBtn: {
                icon: props.closeIcon,
                size: 'x-small',
                variant: 'text'
              }
            }
          }, {
            default: () => [slots.close?.({
              props: closeProps.value,
              timerProgress: progress.value
            })]
          }), hasTimer && props.timer === 'close' && _createVNode(VProgressCircular, {
            "key": "timer",
            "class": "v-alert__timer v-alert__timer--close",
            "modelValue": progress.value,
            "shape": "flat",
            "width": 2
          }, null)])]
        })]
      });
    };
  }
});
//# sourceMappingURL=VAlert.js.map